/**
 * **El personal: los parámetros del año, las tarifas de recargo y cada persona.**
 *
 * De aquí sale la mitad del costo de la pieza, y **todo lo de abajo se puede deshacer** porque son
 * cifras que alguien teclea mirando una hoja: un salario con un cero de más cambia el costo de toda
 * la planta sin que nada se vea raro en esta pantalla.
 *
 * Es un tema solo porque las seis mutaciones refrescan lo mismo —nómina, personas, resumen y
 * estructura—: ese refresco compartido es lo que las ata, y es `useRefreshPersonnel`.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from "@/lib/api";
import { getUndoGeneration, pick, recordChange } from "@/lib/undo";

import { keys } from "./claves";
import { cached, changedKeys, useUndoable } from "./deshacer";
import type { ClassificationChanges } from "./estructura";

import type { Behavior, CostElementCode, Employee, LaborSummary, PayrollRate, PayrollSettings, SalaryReference, Shares, TeamCode } from "../types";

// ── Personal ──────────────────────────────────────────────
export const usePayrollSettings = () =>
  useQuery({ queryKey: keys.payroll, queryFn: () => apiGet<PayrollSettings>("/payroll/settings") });

/** Escalera del salario mínimo del año: la base con la que se calcula cada persona. */
export const useSalaryReference = () =>
  useQuery({ queryKey: keys.salaryReference, queryFn: () => apiGet<SalaryReference>("/payroll/reference") });

/**
 * **Las tarifas de recargo: lo que vale una hora extra.**
 *
 * No tenían pantalla, y de ellas depende que la pantalla de horas extra pueda poner una cifra: sin
 * la tarifa del año, una salida a las once de la noche se queda en «no se puede valorar». Eran la
 * hoja de novedades de Elkin y ahora se editan aquí.
 */
export const usePayrollRates = () =>
  useQuery({ queryKey: keys.payrollRates, queryFn: () => apiGet<PayrollRate[]>("/payroll/rates") });

type RateChange = { id: string; amount: number };

/**
 * Cambiar lo que vale un recargo.
 *
 * **Un año cerrado sella sus tarifas** y el servidor contesta 409: lo que ya se pagó con ellas no
 * se reescribe. Ese texto se enseña tal cual, porque explica algo que no tiene arreglo aquí.
 */
export function useUpdateRate() {
  const client = useQueryClient();
  const patch = ({ id, amount }: RateChange) => apiPatch<null>(`/payroll/rates/${id}`, { amount });
  return useUndoable(
    patch,
    ({ id }) => {
      const tarifa = client.getQueryData<PayrollRate[]>(keys.payrollRates)?.find((r) => r.id === id);
      return tarifa ? { label: `Tarifa de ${tarifa.name}`, undo: () => patch({ id, amount: tarifa.amount }) } : null;
    },
    useRefreshPersonnel(),
  );
}

export const useEmployees = () => useQuery({ queryKey: keys.employees, queryFn: () => apiGet<Employee[]>("/employees") });

export type PersonnelFilters = Partial<Record<"team_code" | "function_code" | "position" | "category" | "cost_element" | "cost_behavior" | "state" | "search", string>>;
/** Totales por equipo/categoría y subtotal de la búsqueda; dinero calculado por la API. */
export const useLaborSummary = (filters: PersonnelFilters = {}) => {
  const query = new URLSearchParams(Object.entries(filters).filter(([, value]) => Boolean(value)));
  return useQuery({ queryKey: [...keys.labor, filters], queryFn: () => apiGet<LaborSummary>(`/labor/summary${query.size ? `?${query}` : ""}`) });
};

function useRefreshPersonnel() {
  const client = useQueryClient();
  return () =>
    Promise.all([
      client.invalidateQueries({ queryKey: keys.payroll }),
      client.invalidateQueries({ queryKey: keys.employees }),
      client.invalidateQueries({ queryKey: keys.labor }),
      client.invalidateQueries({ queryKey: keys.structure }),
      client.invalidateQueries({ queryKey: keys.baseCost().slice(0, 2) }),
      client.invalidateQueries({ queryKey: ["cost-base", "lisa"] }),
    ]);
}

type YearChanges = Partial<{ minimum_wage: number; transport_aid: number; dotation_divisor: number; review_note: string | null }>;

export function useUpdatePayrollYear(year: number) {
  const client = useQueryClient();
  const patch = (body: YearChanges) => apiPatch<null>(`/payroll/settings/${year}`, body);
  return useUndoable(
    patch,
    (body) => {
      const settings = client.getQueryData<PayrollSettings>(keys.payroll);
      if (!settings) return null;
      const before = pick(settings, Object.keys(body)) as YearChanges;
      return { label: `Parámetros de nómina ${year}`, undo: () => patch(before) };
    },
    useRefreshPersonnel(),
  );
}

type ChargeChange = { id: string; rate: number };

export function useUpdateCharge() {
  const client = useQueryClient();
  const patch = ({ id, rate }: ChargeChange) => apiPatch<null>(`/payroll/charges/${id}`, { rate });
  return useUndoable(
    patch,
    ({ id }) => {
      const charge = client.getQueryData<PayrollSettings>(keys.payroll)?.charges.find((c) => c.id === id);
      return charge ? { label: `Porcentaje de ${charge.name}`, undo: () => patch({ id, rate: charge.rate }) } : null;
    },
    useRefreshPersonnel(),
  );
}

type DotationChange = { id: string; unit_price?: number; quantity_per_year?: number };

export function useUpdateDotation() {
  const client = useQueryClient();
  const patch = ({ id, ...body }: DotationChange) => apiPatch<null>(`/payroll/dotation/${id}`, body);
  return useUndoable(
    patch,
    ({ id, ...body }) => {
      const item = client.getQueryData<PayrollSettings>(keys.payroll)?.dotation.find((d) => d.id === id);
      return item ? { label: `Dotación: ${item.name}`, undo: () => patch({ id, ...pick(item, Object.keys(body)) }) } : null;
    },
    useRefreshPersonnel(),
  );
}

export type EmployeeChanges = Partial<{
  allocations: Shares;
  function_code: string | null;
  duties: string | null;
  full_name: string;
  position: string;
  labor_type: "direct" | "indirect";
  team_code: TeamCode;
  pay_scheme: "payroll" | "fixed_fee";
  base_salary: number | null;
  receives_transport_aid: boolean;
  monthly_bonus: number;
  bonus_in_cost: boolean;
  fixed_monthly_cost: number | null;
  is_active: boolean;
  review_note: string | null;
}> &
  ClassificationChanges;

export function useUpdateEmployee(id: string) {
  const client = useQueryClient();
  const patch = (body: EmployeeChanges) => apiPatch<Employee>(`/employees/${id}`, body);
  return useUndoable(
    patch,
    (body) => {
      const person = cached<Employee>(client, keys.employees, id);
      if (!person) return null;
      const fields = changedKeys(body);
      const classification = ["team_code", "labor_type", "cost_element", "cost_behavior", "variable_share", "classification_confirmed"];
      if (fields.some((field) => classification.includes(field))) {
        // Una corrección histórica no ofrece volver a una combinación contradictoria.
        const direct = person.cost_element === "direct_labor";
        if ((person.labor_type === "direct") !== direct || (person.team_code === "crew" && !direct) || (person.team_code === "core" && direct)) return null;
        fields.push(...classification);
      }
      return { label: `Cambio de ${person.full_name}`, undo: () => patch(pick(person, [...new Set(fields)])) };
    },
    useRefreshPersonnel(),
  );
}

/** Datos mínimos para dar de alta a una persona. */
export type NewEmployee = {
  cost_element?: CostElementCode;
  cost_behavior?: Behavior;
  classification_confirmed?: boolean;
  function_code?: string | null;
  duties?: string | null;
  full_name: string;
  position: string;
  labor_type: "direct" | "indirect";
  team_code: TeamCode;
  pay_scheme: "payroll" | "fixed_fee";
  base_salary?: number | null;
  fixed_monthly_cost?: number | null;
  monthly_bonus?: number;
  receives_transport_aid?: boolean;
  /**
   * **Si esta persona prensa.** Y si prensa, en qué prensa: las dos van juntas porque la regla del
   * negocio es que **no existe un artesano sin prensa** (0059). El servidor contesta 422 —«Di en qué
   * prensa trabaja: quien prensa tiene su prensa»— si llega `presses` sin `press_id`.
   *
   * Nació de un fallo real: se creó un artesano sin prensa desde esta misma ficha y nadie se enteró,
   * porque la regla solo se comprobaba cuando alguien marcaba la casilla — y la ficha no la tenía.
   */
  presses?: boolean;
  press_id?: string | null;
};

/**
 * Da de alta a una persona. Deshacer no la borra —la historia no se borra— sino que la deja
 * inactiva: deja de contar en los costos y se puede volver a activar.
 */
export function useCreateEmployee() {
  const refresh = useRefreshPersonnel();
  return useMutation({
    mutationFn: async (body: NewEmployee) => {
      const generation = getUndoGeneration();
      const person = await apiPost<Employee>("/employees", body);
      const setActive = (is_active: boolean) => apiPatch<Employee>(`/employees/${person.id}`, { is_active });
      recordChange({
        label: `Alta de ${person.full_name}`,
        undo: () => setActive(false),
        redo: () => setActive(true),
      }, generation);
      return person;
    },
    onSuccess: refresh,
  });
}

/** Archiva la persona sin borrar su ficha, reparto ni historial. Se reactiva desde su ficha. */
export function useArchiveEmployee(id: string) {
  const refresh = useRefreshPersonnel();
  return useMutation({ mutationFn: () => apiDelete(`/employees/${id}`), onSuccess: refresh });
}

export function useSetEmployeeShares(id: string) {
  const client = useQueryClient();
  const put = (shares: Shares) => apiPut<Employee>(`/employees/${id}/allocations`, { shares });
  return useUndoable(
    put,
    () => {
      const person = cached<Employee>(client, keys.employees, id);
      return person ? { label: `Reparto de ${person.full_name}`, undo: () => put(person.allocations) } : null;
    },
    useRefreshPersonnel(),
  );
}
